import { formatNumber } from '@trmediaab/zebra-utils';
import autobind from 'class-autobind';

import BetType from 'atg/api/BetType';
import { createHelpIcon } from 'atg/components/HelpIcon';
import * as Utils from 'atg/utils/Utils';
import StickyTemplate from 'atg/vendor/StickyTemplate/1.0/StickyTemplate';
import { settings, user } from 'shared/redux/selectors';
import { getStore } from 'shared/redux/store';
import { formatMoney } from 'shared/utils/money';
import * as Text from 'shared/utils/Text';

import BaseView from '../BaseView';
import handleResultUpdates from './handleResultUpdates';

const $ = window.$;

export default class RankingView extends BaseView {
  constructor() {
    super(...arguments);
    autobind(this, RankingView.prototype);
  }

  getGridClass() {
    return 'view-ranking';
  }

  // ---------------------------------------- MENU ----------------------------------------

  getViewTitle() {
    return Text.get('view-ranking');
  }

  getMenuTitle() {
    return Text.get('view-ranking');
  }

  // ---------------------------------------- GRID ----------------------------------------

  onRowWasUpdated() {}

  getGridCellInfo(position, rowCache) {
    const api = this.getAPI();

    let race;
    let betRow;
    let gameRow;
    let resultRow;
    let stakeRow;
    let nrOfBetsRow;
    let betConfig;
    let rowsLeftRow;
    let allPrevShowing;
    let futureRowsLeft;
    let futurePercentLeft;

    if (rowCache) {
      race = rowCache.race;
      betRow = rowCache.betRow;
      gameRow = rowCache.gameRow;
      resultRow = rowCache.resultRow;
      stakeRow = rowCache.stakeRow;
      betConfig = rowCache.betConfig;
      rowsLeftRow = rowCache.rowsLeftRow;
      nrOfBetsRow = rowCache.nrOfBetsRow;
      allPrevShowing = rowCache.allPrevShowing;
      futureRowsLeft = rowCache.futureRowsLeft;
      futurePercentLeft = rowCache.futurePercentLeft;
    } else {
      // eslint-disable-next-line no-param-reassign
      rowCache = {};
      race = rowCache.race = api.raceInformation.getRace(position.row);
      betRow = rowCache.betRow = api.betInformation.getRow(position.row);
      gameRow = rowCache.gameRow = api.gameInformation.getRow(position.row);
      resultRow = rowCache.resultRow = api.resultInformation.getRow(
        position.row,
      );
      stakeRow = rowCache.stakeRow = api.stake.getRow(position.row);
      nrOfBetsRow = rowCache.nrOfBetsRow = api.stake.getNrOfBetsRow(
        position.row,
      );
      betConfig = rowCache.betConfig = api.betInformation.getBetConfig();
      rowsLeftRow = rowCache.rowsLeftRow = api.rowsLeftInformation.getRow(
        position.row + 1,
      );
      allPrevShowing = rowCache.allPrevShowing = Array.from({
        length: position.row,
      }).every((_, idx) => betConfig.getShowResultRow(idx));

      let rowsLeftTargetLegIndex = 0;
      for (let i = 1; i <= api.rowsLeftInformation.getNumberOfRows(); i++) {
        if (
          // If the candidate row is either hidden, or not yet finished, and
          (!api.resultInformation.getRow(i) ||
            !betConfig.getShowResultRow(i)) &&
          // all previous rows are showing and finished
          Array.from({ length: i }).every(
            (_, idx) =>
              betConfig.getShowResultRow(idx) &&
              api.resultInformation.getRow(idx),
          )
        ) {
          // then use this as the rows left leg index
          rowsLeftTargetLegIndex = i;
          break;
        }
      }

      if (rowsLeftTargetLegIndex > 0 && rowsLeftTargetLegIndex < position.row) {
        const futureRowsLeftRow = api.rowsLeftInformation.getRow(
          rowsLeftTargetLegIndex + 1,
        );
        if (
          futureRowsLeftRow
            ?.getRowsLeftForLeg(position.row)
            ?.some(value => value > 0)
        ) {
          futureRowsLeft = rowCache.futureRowsLeft =
            futureRowsLeftRow.getRowsLeftForLeg(position.row);
        }
        if (
          futureRowsLeftRow
            ?.getPercentLeftForLeg(position.row)
            ?.some(value => value > 0)
        ) {
          futurePercentLeft = rowCache.futurePercentLeft =
            futureRowsLeftRow.getPercentLeftForLeg(position.row);
        }
      }
    }

    const startNumber = betRow
      ? betRow.getStartNumberAtPosition(position.column)
      : position.column + 1;

    const startIndex = startNumber - 1;
    const start = race.getStart(startIndex);
    const info = {
      rowCache: rowCache,
    };

    if (start) {
      info.number = start.getStartNumber();

      const horse = start.getHorse();
      if (horse) {
        info.horseName = horse.getName();
        info.commentIndicator = this.horseIsInMyHorses(horse);
      }

      const jockey = start.getJockey();
      if (jockey) {
        info.jockeyTrainerName = jockey.getName();
      }

      const originalJockey = start.getOriginalJockey();
      if (originalJockey) {
        info.originalJockey = originalJockey.getName();
      }
    }

    if (
      resultRow &&
      settings.showRankingResults(getStore().getState()) &&
      betConfig.getShowResultRow(position.row)
    ) {
      info.winner = resultRow.isWinner(startIndex);
    }

    const gameRowCellInfo = this.getGameRowCellInfo(gameRow, startIndex);
    Object.assign(info, gameRowCellInfo);

    const bottomInfoParts = [];

    if (betRow) {
      const reserveNumber = betRow.getReserveNumber(startIndex);
      if (reserveNumber) {
        bottomInfoParts.push(
          `<strong class="font-extra-bold">R${reserveNumber}</strong>`,
        );
      }
    }

    info.cellClass = null;
    if (futureRowsLeft && futurePercentLeft) {
      const parts = [];

      const percent = futurePercentLeft[position.column];
      if (percent) {
        parts.push(`${String(Math.round(percent * 1000) / 10)}%`);
      }

      const rowsLeftForHorse = futureRowsLeft[position.column];
      if (rowsLeftForHorse) {
        parts.push(String(Math.round(rowsLeftForHorse * 10) / 10));
      }

      const str = parts.join(' - ') || '-';
      bottomInfoParts.push(str);
    } else if (allPrevShowing && rowsLeftRow && !rowsLeftRow.isEmpty()) {
      const color = rowsLeftRow.getColor();
      if (color) {
        info.cellClass = `rows-left-${rowsLeftRow.getColor()}`;
      }

      const parts = [];

      const percent = rowsLeftRow.getPercentLeft(position.column);
      if (percent) {
        parts.push(`${String(Math.round(percent * 1000) / 10)}%`);
      }

      const rowsLeft = rowsLeftRow.getRowsLeft(position.column);
      if (rowsLeft) {
        parts.push(String(Math.round(rowsLeft * 10) / 10));
      }

      const str = parts.join(' - ') || '-';
      bottomInfoParts.push(str);
    } else if (stakeRow) {
      const percent = stakeRow.getPercentOfBets(position.column);
      if (percent) {
        const nrOfBets = nrOfBetsRow
          ? formatNumber(nrOfBetsRow.getNrOfBets(position.column))
          : undefined;
        bottomInfoParts.push(
          `${String(Math.round(percent * 1000) / 10)}%${
            nrOfBets ? ` - ${nrOfBets}` : ''
          }`,
        );
      }
    }

    info.bottomInfo =
      bottomInfoParts.length > 0
        ? $(`<span>${bottomInfoParts.join(', ')}</span>`)
        : '-';

    return info;
  }

  onGridCellTapped(position) {
    this.getGrid().highlightCell(position);

    const buttons = [
      {
        icon: 'button-last',
        onPress: this.onGridLastButtonPressed,
      },
    ];

    const betKey = this.getAPI().betInformation.getBetKey();
    let betType;
    if (betKey) {
      const betTypeCode = betKey.getBetType();
      betType = BetType.getBetType(betTypeCode);
    }

    if (betType && betType.hasSpares() && ALLOW_SPARES) {
      buttons.push(
        {
          title: 'R1',
          onPress: this.onGridReserve1ButtonPressed,
        },
        {
          title: 'R2',
          onPress: this.onGridReserve2ButtonPressed,
        },
      );
    }

    buttons.push(...this.makeHorseButtons(position));

    if (
      INCLUDE_TODAYS_BEST_BET &&
      user.hasRole('ROLE_TODAYS_BEST_BET_EDITOR')(getStore().getState())
    ) {
      // Admin users get here, and potentially get admin buttons
      this.getAPI().loadBetKeyInfo(betKey, betKeyInfo => {
        // Only add admin buttons for tracks without "/".
        // A track name with a "/" means at least two different tracks,
        // and odds values for Todays Best Bet cannot be extracted
        // properly from those bets.
        if (!betKeyInfo.getTrack().getName().includes('/')) {
          buttons.push(
            {
              title: 'Vinner',
              extraClassNames: ['first-best-bet-button'],
              onPress: function () {
                Utils.callDelegate(
                  this.delegate,
                  'onTodaysBestBetPressed',
                  position,
                );
              }.bind(this),
            },
            {
              title: 'Radera',
              extraClassNames: ['best-bet-button--delete'],
              onPress: function () {
                Utils.callDelegate(
                  this.delegate,
                  'onDeleteTodaysBestBetPressed',
                );
              }.bind(this),
            },
          );
        }
        this.getGrid().showCellButtons(position, buttons);
      });
    } else {
      // Normal users get here
      this.getGrid().showCellButtons(position, buttons);
    }
  }

  onGridReserve1ButtonPressed(position) {
    this.onGridReserveButtonPressed(position, 1);
  }

  onGridReserve2ButtonPressed(position) {
    this.onGridReserveButtonPressed(position, 2);
  }

  onGridReserveButtonPressed(position, reserveNumber) {
    const willShowLockedAlert = this.showAlertWhenLocked();
    const grid = this.getGrid();
    grid.closeHighlight();

    if (!willShowLockedAlert) {
      const betRow = this.getAPI().betInformation.getRow(position.row);
      const startNumber = betRow.getStartNumberAtPosition(position.column);
      const startIndex = startNumber - 1;
      const numberOfStarts = this.getAPI()
        .raceInformation.getRace(position.row)
        .getNumberOfStarts();

      betRow.setReserveNumber(startIndex, reserveNumber, numberOfStarts);

      grid.updateRow(position.row);

      this.getAPI().scheduleBetUpdate();
    }
  }

  // ---------------------------------------- BOTTOM INFO DELEGATE METHODS ----------------------------------------

  getBottomInfoTitle() {
    return Text.get('bottom-info-header-tab-title-results');
  }

  getBottomInfoContent() {
    const $conditions = (this.$conditions = StickyTemplate.jq(
      'bottom-conditions-ranking',
    ));

    Utils.insertTexts($conditions);

    $conditions.find('.ranking-results-heading').append(
      createHelpIcon({
        header: Text.get('help-ranking-header'),
        body: Text.get('help-ranking-body'),
      }),
    );

    this.updateResults();
    this.updateMyDividend();

    $conditions
      .find('#blue-key')
      .text(Text.get('bottom-conditions-legend-blue-key'));
    $conditions
      .find('#blue-desc')
      .text(Text.get('bottom-conditions-legend-blue-desc'));

    $conditions
      .find('#green-key')
      .text(Text.get('bottom-conditions-legend-green-key'));
    $conditions
      .find('#green-desc')
      .text(Text.get('bottom-conditions-legend-green-desc'));

    $conditions
      .find('#red-key')
      .text(Text.get('bottom-conditions-legend-red-key'));
    $conditions
      .find('#red-desc')
      .text(Text.get('bottom-conditions-legend-red-desc'));

    $conditions
      .find('#grey-key')
      .text(Text.get('bottom-conditions-legend-grey-key'));
    $conditions
      .find('#grey-desc')
      .text(Text.get('bottom-conditions-legend-grey-desc'));

    $conditions
      .find('#legend-label')
      .text(Text.get('bottom-conditions-legend-label'));
    return $conditions;
  }

  updateResults() {
    const $conditions = this.$conditions;
    const hasResults = handleResultUpdates(
      this.getAPI(),
      $conditions,
      this.onShowResultRowCheckboxChanged,
      this.onShowAllResultRowsPressed,
      this.onHideAllResultRowsPressed,
    );
    const $noResult = $conditions.find('.no-result');
    const $showInfo = $conditions.find('.ranking-results-show-info');

    if (hasResults) {
      $noResult.hide();
      $showInfo.show();
    } else {
      $noResult.show();
      $showInfo.hide();
    }
  }

  updateMyDividend() {
    const $conditions = this.$conditions;
    const api = this.getAPI();
    const resultInformation = api.resultInformation;
    const raceInformation = api.raceInformation;
    const myDividendInformation = api.myDividendInformation;
    const betInformation = api.betInformation;
    const betConfig = betInformation.getBetConfig();
    let n, $row;

    const $myDividends = $conditions.find('.my-dividends').hide();

    if (resultInformation.isResultComplete()) {
      const resultType = resultInformation.getResultType(),
        numberOfRows = raceInformation.getNumberOfRaces();

      if (resultType !== 'MarkingBet' && resultType !== 'FivePlus') {
        const $tableHeader = $myDividends.find('table thead');
        $tableHeader.empty();
      }

      let showAllRows = true;
      const $tableBody = $myDividends.find('table tbody');
      $tableBody.empty();

      const $rikstotoDividendNotice = $myDividends
        .find('.rikstoto-dividend-notice')
        .hide();
      if (myDividendInformation.getCheckRikstoto()) {
        $myDividends
          .find('.rikstoto-dividend-notice')
          .text(Text.get('ranking-my-dividends-rikstoto-dividend-notice'));
        $rikstotoDividendNotice.show();
      } else {
        const allDividendData =
          resultType === 'FivePlus'
            ? myDividendInformation.getBreakdown()
            : myDividendInformation.getAllData();

        for (n = 0; n < allDividendData.length; n++) {
          if (allDividendData[n] == null) {
            continue;
          }

          const partSum = allDividendData[n].getPartSum();
          const nrOfRows = `${allDividendData[n].getNrOfRows()}`.replace(
            '.',
            ',',
          );
          let numberOfRowsText;
          if (resultType === 'FivePlus') {
            const baseString =
              nrOfRows === '1'
                ? 'ranking-my-dividends-rows-with-prize-group'
                : 'ranking-my-dividends-rows-with-prize-group-plural';
            let prizeGroupString;
            switch (allDividendData[n].getPrizeGroup()) {
              case 'FiveInOrderPrize':
                prizeGroupString = Text.get(
                  'ranking-dividends-fiveplus-all-correct',
                );
                break;
              case 'FiveInAnyOrderPrize':
                prizeGroupString = Text.get(
                  'ranking-dividends-fiveplus-first-n-correct',
                  5,
                );
                break;
              case 'FourOfFivePrize':
                prizeGroupString = Text.get(
                  'ranking-dividends-fiveplus-four-correct',
                );
                break;
              case 'ThreeInOrderPrize':
                prizeGroupString = Text.get(
                  'ranking-dividends-fiveplus-first-n-correct',
                  3,
                );
                break;
              default:
                break;
            }
            numberOfRowsText = Text.get(baseString, nrOfRows, prizeGroupString);
          } else {
            const textString =
              nrOfRows === '1'
                ? 'ranking-my-dividends-rows-with-correct'
                : 'ranking-my-dividends-rows-with-correct-plural';
            const rowsWith = numberOfRows - n;
            numberOfRowsText = Text.get(textString, nrOfRows, rowsWith);
          }

          $row = StickyTemplate.jq(
            'bottom-conditions-ranking-my-dividends-row',
            {
              '.number-of-rows': numberOfRowsText,
              '.dividend': formatMoney(partSum.getSum(), partSum.getCurrency()),
            },
          );

          $tableBody.append($row);
        }

        const total = myDividendInformation.getTotalSum();

        if (total != null) {
          $row = StickyTemplate.jq(
            'bottom-conditions-ranking-my-dividends-sum',
            {
              '.total': Text.get('ranking-my-dividends-total'),
              '.dividend': formatMoney(total.getSum(), total.getCurrency()),
            },
          );
          $tableBody.append($row);
        }

        const submitPrice = myDividendInformation.getSubmitPrice();
        const netResult = myDividendInformation.getNetResult();

        if (
          (resultType === 'MarkingBet' || resultType === 'FivePlus') &&
          (submitPrice || netResult)
        ) {
          $row = StickyTemplate.jq(
            'bottom-conditions-ranking-my-dividends-separator',
          );
          $tableBody.append($row);
        }

        if (submitPrice != null) {
          $row = StickyTemplate.jq(
            'bottom-conditions-ranking-my-dividends-extra',
            {
              '.value': Text.get('ranking-my-dividends-submit-price'),
              '.dividend': formatMoney(
                submitPrice.getSum(),
                submitPrice.getCurrency(),
              ),
            },
          );
          $tableBody.append($row);
        }

        if (netResult != null) {
          $row = StickyTemplate.jq(
            'bottom-conditions-ranking-my-dividends-extra',
            {
              '.value': Text.get('ranking-my-dividends-net-result'),
              '.dividend': formatMoney(
                netResult.getSum(),
                netResult.getCurrency(),
              ),
            },
          );
          $tableBody.append($row);
        }
      }

      for (n = 0; n < numberOfRows; n++) {
        if (false === betConfig.getShowResultRow(n)) {
          showAllRows = false;
        }
      }

      if (showAllRows) {
        $myDividends.show();
      }
    }
  }

  onShowResultRowCheckboxChanged(event) {
    const $checkbox = $(event.currentTarget);
    const rowIndex = $checkbox.data('row-index');
    const checked = $checkbox.is(':checked');

    const api = this.getAPI();
    const betInformation = api.betInformation;
    const betConfig = betInformation.getBetConfig();
    const numRows = betInformation.getNumberOfRows();

    betConfig.setShowResultRow(rowIndex, checked);

    this.updateResults();
    this.updateMyDividend();

    const grid = this.getGrid();

    for (let i = rowIndex; i < numRows; i++) {
      grid.updateRow(i); // Update the row and all subsequent rows since their rowsLeft is affected
    }
  }

  onShowAllResultRowsPressed(event) {
    this.toggleAllResultRows(event, true);
  }

  onHideAllResultRowsPressed(event) {
    this.toggleAllResultRows(event, false);
  }

  toggleAllResultRows(event, checked) {
    const api = this.getAPI();
    const betInformation = api.betInformation;
    const betConfig = betInformation.getBetConfig();

    const $link = $(event.currentTarget);
    const $container = $link.closest('.table-container');

    const grid = this.getGrid();

    $container.find('input[type=checkbox]').each(function () {
      const $checkbox = $(this);
      const rowIndex = $checkbox.data('row-index');

      betConfig.setShowResultRow(rowIndex, checked);
      grid.updateRow(rowIndex);
    });

    this.updateResults();
    this.updateMyDividend();
  }
}
